import type {
  ApiDemoScenarioId,
  ApiRequestPhase,
  CapabilitySummary,
  ChartCardScenarioId,
  GlobalChartScenarioId,
  GenderBoxPlotGender,
  InfiniteRenderStatus,
  Locale,
  NavigationItem,
  PortfolioGridImpact,
  PortfolioGridStatus,
  ProblemSummary,
  RoadmapStatus,
} from "../types/dashboard";

type WorkflowNodeKey = "A" | "C" | "D2" | "A-1" | "C-1" | "D2-1";

export interface ChartCardCopy {
  title: string;
  description: string;
  ariaLabel: string;
  fallbackDescription: string;
}

export interface SectionCopy {
  eyebrow: string;
  title: string;
  description: string;
}

export interface SankeyCopy {
  ariaLabel: string;
  legendLabel: string;
  fallbackDescription: string;
  emptyTitle: string;
  emptyDescription: string;
  previousYear: string;
  currentYear: string;
  unitLabel: string;
  tooltipUnitLabel: string;
  numberLocale: string;
  nodeLabels: Record<WorkflowNodeKey, string>;
  legendItems: {
    veryImproved: string;
    improved: string;
    noChange: string;
    worsened: string;
    veryWorsened: string;
  };
}

interface ChartEmptyLabelCopy {
  emptyLabel: string;
}

export interface ChartOptionLabels {
  businessTrend: ChartEmptyLabelCopy & {
    revenueIndex: string;
    activeUsers: string;
    conversionRate: string;
    revenueAxis: string;
    conversionAxis: string;
  };
  implementationTrend: ChartEmptyLabelCopy & {
    previous: string;
    current: string;
    reviewScore: string;
    casesAxis: string;
    reviewAxis: string;
  };
  qualityScatter: ChartEmptyLabelCopy & {
    cycleTimeAxis: string;
    defectRateAxis: string;
    complexityHigh: string;
    complexityLow: string;
    seriesName: string;
  };
  genderBoxPlot: ChartEmptyLabelCopy & {
    valueAxis: string;
    seriesName: string;
    numberLocale: string;
    genderLabels: Record<GenderBoxPlotGender, string>;
    statsLabels: {
      min: string;
      q1: string;
      median: string;
      q3: string;
      max: string;
      outlier: string;
    };
  };
  categoryShare: ChartEmptyLabelCopy & {
    centerLabel: string;
    seriesName: string;
    numberLocale: string;
  };
}

export interface ChartScenarioCopy {
  label: string;
  cardLabel: string;
  options: Record<
    GlobalChartScenarioId,
    {
      label: string;
      description: string;
    }
  >;
  edgeCaseOptions: Record<
    ChartCardScenarioId,
    {
      label: string;
      description: string;
    }
  >;
}

export interface TimelineCopy {
  editorLabel: string;
  editorTitle: string;
  editorDescription: string;
  visibleLabel: string;
  titleLabel: string;
  groupLabel: string;
  statusLabel: string;
  startLabel: string;
  endLabel: string;
  progressLabel: string;
  shellLabel: string;
  summary: (visibleCount: number, groupCount: number) => string;
  zoomControlsLabel: string;
  zoomInLabel: string;
  zoomOutLabel: string;
  resetLabel: string;
  sidebarLabel: string;
  axisHeaderLabel: string;
  momentLocale: string;
  markerLabels: {
    now: string;
    today: string;
  };
  statusLabels: Record<RoadmapStatus, string>;
}

export interface CustomGridCopy {
  ariaLabel: string;
  searchLabel: string;
  searchPlaceholder: string;
  categoryLabel: string;
  allCategoriesLabel: string;
  selectedLabel: (count: number) => string;
  editModeLabel: string;
  editModeNote: string;
  cancelLabel: string;
  saveLabel: string;
  addRowLabel: string;
  addChildLabel: string;
  deleteSelectedLabel: string;
  newRowLabel: string;
  newChildLabel: string;
  newRowPlaceholder: {
    capability: string;
    category: string;
    owner: string;
    coverage: string;
  };
  newChildPlaceholder: {
    name: string;
    owner: string;
  };
  clearLabel: string;
  expandLabel: string;
  collapseLabel: string;
  selectAllLabel: string;
  selectRowLabel: (capability: string) => string;
  toggleRowLabel: (action: string, capability: string) => string;
  headers: {
    capability: string;
    category: string;
    owner: string;
    status: string;
    coverage: string;
    updated: string;
  };
  impactSuffix: string;
  addedChildNote: string;
  statusLabels: Record<PortfolioGridStatus, string>;
  impactLabels: Record<PortfolioGridImpact, string>;
}

export interface TableHeaders {
  project: string;
  domain: string;
  status: string;
  leadTime: string;
  chartCoverage: string;
  apiContract: string;
  updatedAt: string;
  leadTimeUnitLabel: string;
  statusLabels: Record<"Stable" | "Improving" | "Watch", string>;
}

export interface InfiniteTableCopy {
  title: string;
  description: string;
  ariaLabel: string;
  loadingLabel: string;
  loadingMoreLabel: string;
  loadMoreLabel: string;
  endLabel: string;
  emptyLabel: string;
  rowCountLabel: (count: number, total: number) => string;
  headers: {
    screen: string;
    module: string;
    status: string;
    requestCount: string;
    loadedAt: string;
  };
  requestCountUnitLabel: string;
  statusLabels: Record<InfiniteRenderStatus, string>;
}

export interface PublicDataApiCopy {
  sourceBadge: string;
  endpointBadge: string;
  contractBadge: string;
  searchCard: {
    title: string;
    description: string;
  };
  responseCard: {
    title: string;
    description: string;
  };
  failureDemo: {
    label: string;
    description: string;
    emptyKeyword: string;
    serviceErrorCodeLabel: string;
    unavailableErrorTitle: string;
    unavailableErrorDescription: string;
    unavailableErrorListLabel: string;
    retryLabel: string;
    liveModeLabel: string;
    options: Record<
      ApiDemoScenarioId,
      {
        label: string;
        description: string;
        logMessage: string;
        recovery: string;
        tooltip?: string;
      }
    >;
  };
  requestLog: {
    title: string;
    description: string;
    labels: {
      endpoint: string;
      scenario: string;
      phase: string;
      statusCode: string;
      errorCode: string;
      retryable: string;
      message: string;
      recovery: string;
    };
    idleMessage: string;
    loadingMessage: string;
    successMessage: (count: number, total: number) => string;
    emptyMessage: string;
    retryableYes: string;
    retryableNo: string;
    notApplicable: string;
    noActionNeeded: string;
  };
  loadMoreLabel: string;
  loadingLabel: string;
  loadingMoreLabel: string;
  idleTitle: string;
  idleDescription: string;
  errorTitle: string;
  canceledTitle: string;
  canceledDescription: string;
  endLabel: string;
  phaseLabels: Record<ApiRequestPhase, string>;
  metaLabels: {
    requestId: string;
    keyword: string;
    page: string;
    latency: string;
    startedAt: string;
    completedAt: string;
  };
  latencyUnit: string;
  publicData: {
    keywordLabel: string;
    keywordPlaceholder: string;
    searchLabel: string;
    noticeTitle: string;
    noticeDescription: string;
    emptyTitle: string;
    emptyDescription: string;
    missingKeyTitle: string;
    missingKeyDescription: string;
    imageFallback: string;
    addressFallback: string;
    contentTypeLabel: string;
    contentTypeCodeLabel: string;
    multilingualCodeLabel: string;
    regionLabel: string;
    categoryLabel: string;
    classificationLabel: string;
    legalDistrictLabel: string;
    legalRegionCodeLabel: string;
    legalDistrictCodeLabel: string;
    modifiedLabel: string;
    detailEyebrow: string;
    detailLoadingLabel: string;
    detailErrorTitle: string;
    addressDetailLabel: string;
    overviewLabel: string;
    phoneLabel: string;
    createdLabel: string;
    homepageLabel: string;
    zipCodeLabel: string;
    coordinatesLabel: string;
    coordinatesFallback: string;
    mapLinkLabel: string;
    mapLinkAriaLabel: (title: string) => string;
    languageNoticeLabel: string;
    languageNoticeTooltip: string;
    detailCloseLabel: string;
    totalLabel: (count: number, total: number) => string;
    sourceLabel: string;
    detailSourceLabel: string;
  };
}

export interface PublishedWorksCopy {
  roleLabel: string;
  technologiesLabel: string;
  implementationPointsLabel: string;
  openLinkLabel: string;
  unavailableLinkLabel: string;
  demoRoleLabel: string;
  publishedRoleLabel: string;
}

export interface ComponentLabCopy {
  positioningTitle: string;
  positioningDescription: string;
  positioningItems: string[];
  fieldCard: {
    title: string;
    description: string;
  };
  validationCard: {
    title: string;
    description: string;
  };
  choiceCard: {
    title: string;
    description: string;
  };
  previewCard: {
    title: string;
    description: string;
  };
  fields: {
    keywordLabel: string;
    keywordPlaceholder: string;
    keywordHelper: string;
    textLabel: string;
    textPlaceholder: string;
    textHelper: string;
    textareaLabel: string;
    textareaPlaceholder: string;
    textareaHelper: string;
    numberLabel: string;
    numberHelper: string;
    selectLabel: string;
    selectHelper: string;
    disabledLabel: string;
    disabledValue: string;
    disabledHelper: string;
    requiredLabel: string;
    requiredPlaceholder: string;
    requiredError: string;
    checkboxLabel: string;
    checkboxHelper: string;
    switchLabel: string;
    switchHelper: string;
    segmentedLabel: string;
    segmentedHelper: string;
    radioLegend: string;
    radioHelper: string;
  };
  selectOptions: {
    value: string;
    label: string;
  }[];
  radioOptions: {
    value: string;
    label: string;
    description: string;
  }[];
  segmentedOptions: {
    value: string;
    label: string;
  }[];
  previewLabels: {
    componentApi: string;
    accessibility: string;
    responsive: string;
    validation: string;
  };
}

export interface DashboardDictionary {
  appName: string;
  appSubtitle: string;
  nav: NavigationItem[];
  navigationLabel: string;
  homeLabel: string;
  heroEyebrow: string;
  heroTitle: string;
  heroBody: string;
  heroCtaLabel: string;
  heroRefreshLabel: string;
  portfolioLinkLabel: string;
  portfolioFocusLabel: string;
  portfolioFocus: string[];
  previewLabel: string;
  previewTechLabel: string;
  updatedLabel: string;
  dateTimeLocale: string;
  heroStats: {
    chartCases: string;
    timelineItems: string;
    gridRows: string;
  };
  languageLabel: string;
  refreshLabel: string;
  loading: string;
  errorTitle: string;
  homeProblems: ProblemSummary[];
  homeCapabilities: CapabilitySummary[];
  chartCards: {
    businessTrend: ChartCardCopy;
    implementationTrend: ChartCardCopy;
    qualityScatter: ChartCardCopy;
    genderBoxPlot: ChartCardCopy;
    capabilityTreemap: ChartCardCopy;
    categoryShare: ChartCardCopy;
    sankey: {
      title: string;
      description: string;
    };
  };
  chartOptions: ChartOptionLabels;
  chartScenarios: ChartScenarioCopy;
  sankey: SankeyCopy;
  timeline: TimelineCopy;
  customGrid: CustomGridCopy;
  apiPlayground: PublicDataApiCopy;
  dataGridCard: {
    title: string;
    description: string;
  };
  publishedWorks: PublishedWorksCopy;
  componentLab: ComponentLabCopy;
  pages: {
    charts: SectionCopy;
    dataGrid: SectionCopy;
    timeline: SectionCopy;
    apiPlayground: SectionCopy;
    componentLab: SectionCopy;
    works: SectionCopy;
  };
  demoRoutes: SectionCopy & {
    openLabel: string;
  };
  sections: {
    overview: SectionCopy;
    skills: SectionCopy;
    charts: SectionCopy;
    timeline: SectionCopy;
    table: SectionCopy;
    apiPlayground: SectionCopy;
    componentLab: SectionCopy;
    publishedWorks: SectionCopy;
  };
  tableHeaders: TableHeaders;
  infiniteTable: InfiniteTableCopy;
}

export const dictionary: Record<Locale, DashboardDictionary> = {
  ko: {
    appName: "류호진",
    appSubtitle: "프론트엔드 포트폴리오",
    navigationLabel: "주요 페이지",
    homeLabel: "홈으로 이동",
    heroEyebrow: "프론트엔드 포트폴리오",
    heroTitle: "실무에서 자주 만나는 데이터 UI를 직접 구현했습니다",
    heroBody:
      "차트, 일정, 그리드가 섞인 운영형 UI를 React와 TypeScript로 재구성했습니다. 실제 데이터 없이도 화면 설계와 컴포넌트 책임이 보이도록 만든 포트폴리오 데모입니다.",
    heroCtaLabel: "기능별 데모 보기",
    heroRefreshLabel: "데모 데이터 다시 불러오기",
    portfolioLinkLabel: "Notion 포트폴리오",
    portfolioFocusLabel: "포트폴리오 구현 포커스",
    portfolioFocus: [
      "차트 공통화",
      "커스텀 UI",
      "간트 타임라인",
      "커스텀 그리드",
    ],
    previewLabel: "포트폴리오 화면 미리보기",
    previewTechLabel: "React + TypeScript",
    updatedLabel: "업데이트",
    dateTimeLocale: "ko-KR",
    heroStats: {
      chartCases: "차트 예제",
      timelineItems: "일정 항목",
      gridRows: "그리드 행",
    },
    languageLabel: "언어 변경",
    refreshLabel: "데이터 새로고침",
    loading: "데이터를 불러오는 중입니다.",
    errorTitle: "데이터를 불러오지 못했습니다.",
    homeProblems: [
      {
        id: "dense-data-ui",
        label: "복합 데이터 화면",
        value: "읽기 쉬운 배치",
        helper:
          "차트, 테이블, 상태 요약이 함께 있어도 정보 우선순위가 흐려지지 않게 구성합니다.",
      },
      {
        id: "editable-grid-flow",
        label: "편집형 그리드 흐름",
        value: "예측 가능한 상태",
        helper:
          "검색, 필터, 선택, 편집 모드가 동시에 움직여도 조작 흐름을 유지합니다.",
      },
      {
        id: "timeline-workbench",
        label: "일정 조작 화면",
        value: "이동과 리사이즈",
        helper:
          "기간 데이터, 표시 범위, 항목 편집을 분리해 timeline 조작을 안정화합니다.",
      },
      {
        id: "responsive-safety",
        label: "반응형 안정성",
        value: "가로 넘침 방지",
        helper:
          "차트와 그리드가 작은 화면에서도 읽히도록 스크롤과 fallback을 준비합니다.",
      },
    ],
    homeCapabilities: [
      {
        id: "component-boundaries",
        category: "컴포넌트 책임 분리",
        highlights: [
          "섹션은 조립 담당",
          "기능 모듈은 구현 담당",
          "공통 Form UI 직접 구현",
        ],
      },
      {
        id: "data-shaping",
        category: "데이터 가공 구조",
        highlights: ["모의 API 경계", "화면용 데이터 가공", "명확한 응답 타입"],
      },
      {
        id: "interactive-state",
        category: "상호작용 상태 설계",
        highlights: ["차트 범례 상태", "그리드 편집 모드", "타임라인 조작"],
      },
      {
        id: "accessibility-responsive",
        category: "접근성과 반응형",
        highlights: ["접근성 라벨", "대체 설명", "모바일 스크롤 보호"],
      },
    ],
    chartCards: {
      businessTrend: {
        title: "막대 + 선형 차트",
        description:
          "월별 지표와 전환율을 하나의 옵션 생성 함수에서 생성합니다.",
        ariaLabel: "월별 매출, 활성 사용자, 전환율 차트",
        fallbackDescription:
          "월별 지표를 막대와 라인으로 함께 비교하는 차트입니다.",
      },
      implementationTrend: {
        title: "차트 구현 추이",
        description:
          "전년도 비교 막대와 리뷰 점수를 함께 보여주고, 긴 데이터는 dataZoom으로 탐색합니다.",
        ariaLabel: "포트폴리오 차트 구현 추이",
        fallbackDescription:
          "이전 값과 현재 값을 비교하고 리뷰 점수를 라인으로 표시하는 복합 차트입니다.",
      },
      qualityScatter: {
        title: "품질 산점도",
        description:
          "처리 기간, 결함률, 복잡도를 scatter chart와 visualMap으로 함께 비교합니다.",
        ariaLabel: "기능 품질과 복잡도 scatter chart",
        fallbackDescription:
          "각 기능의 처리 기간, 결함률, 복잡도를 점 위치와 색상으로 비교하는 산점도입니다.",
      },
      genderBoxPlot: {
        title: "성별 박스플롯",
        description:
          "성별과 연령 그룹별 분포를 boxplot과 outlier 점으로 비교하는 더미 예제입니다.",
        ariaLabel: "성별과 연령 그룹별 분포 boxplot chart",
        fallbackDescription:
          "성별과 연령 그룹별 최솟값, 사분위수, 중앙값, 최댓값, 이상치를 비교하는 boxplot 차트입니다.",
      },
      capabilityTreemap: {
        title: "역량 트리맵",
        description:
          "차트 아카이브의 구현 영역을 크기와 색상 강도로 재구성한 treemap 예제입니다.",
        ariaLabel: "포트폴리오 시각화 역량 treemap",
        fallbackDescription:
          "ECharts, 마크업 차트, 공통 UX 항목의 비중을 treemap으로 표시합니다.",
      },
      categoryShare: {
        title: "파이 차트",
        description:
          "범례와 라벨이 있는 pie chart로 차트 예제 분포를 요약합니다.",
        ariaLabel: "차트 카테고리 비율 pie chart",
        fallbackDescription:
          "차트 예제 카테고리별 비율을 도넛 차트로 표시합니다.",
      },
      sankey: {
        title: "커스텀 컴포넌트 + Sankey 흐름",
        description:
          "전년 대비 판정현황 화면을 참고해 좌우 상태 막대와 흐름선, 범례, 수치를 분리해 구성했습니다.",
      },
    },
    chartOptions: {
      businessTrend: {
        emptyLabel: "표시할 차트 데이터가 없습니다.",
        revenueIndex: "매출 지수",
        activeUsers: "활성 사용자",
        conversionRate: "전환율",
        revenueAxis: "매출",
        conversionAxis: "전환",
      },
      implementationTrend: {
        emptyLabel: "표시할 차트 데이터가 없습니다.",
        previous: "이전",
        current: "현재",
        reviewScore: "리뷰 점수",
        casesAxis: "건수",
        reviewAxis: "리뷰",
      },
      qualityScatter: {
        emptyLabel: "표시할 차트 데이터가 없습니다.",
        cycleTimeAxis: "처리 기간",
        defectRateAxis: "결함률",
        complexityHigh: "높은 복잡도",
        complexityLow: "낮음",
        seriesName: "기능 품질",
      },
      genderBoxPlot: {
        emptyLabel: "표시할 차트 데이터가 없습니다.",
        valueAxis: "지표값",
        seriesName: "성별 분포",
        numberLocale: "ko-KR",
        genderLabels: {
          Male: "남성",
          Female: "여성",
        },
        statsLabels: {
          min: "최솟값",
          q1: "1사분위",
          median: "중앙값",
          q3: "3사분위",
          max: "최댓값",
          outlier: "이상치",
        },
      },
      categoryShare: {
        emptyLabel: "표시할 차트 데이터가 없습니다.",
        centerLabel: "예제",
        seriesName: "차트 아카이브",
        numberLocale: "ko-KR",
      },
    },
    chartScenarios: {
      label: "차트 시나리오",
      cardLabel: "카드별 시나리오",
      options: {
        normal: {
          label: "기본",
          description: "새로고침할 때마다 더미 데이터가 자연스럽게 달라집니다.",
        },
        empty: {
          label: "빈 데이터",
          description: "모든 차트의 빈 상태를 한 번에 확인합니다.",
        },
      },
      edgeCaseOptions: {
        normal: {
          label: "기본",
          description: "기본 더미 데이터를 표시합니다.",
        },
        missingXAxis: {
          label: "일부 월 누락",
          description: "랜덤 월의 값을 비웁니다.",
        },
        zeroValues: {
          label: "0값 포함",
          description: "랜덤 항목을 0으로 표시합니다.",
        },
        singleSlice: {
          label: "단일 파이 조각",
          description: "파이 조각 하나만 표시합니다.",
        },
        smallSankeyValues: {
          label: "작은 Sankey 값",
          description: "작은 흐름값이 몰린 상태를 표시합니다.",
        },
        outlierHeavyBoxplot: {
          label: "이상치 많은 박스플롯",
          description: "이상치가 많은 분포를 표시합니다.",
        },
      },
    },
    sankey: {
      ariaLabel: "전년 대비 판정현황 sankey chart",
      legendLabel: "Sankey 범례",
      fallbackDescription:
        "2025년 A, C, D2 상태가 2026년 A, C, D2 상태로 이동한 비율을 흐름선으로 표시합니다.",
      emptyTitle: "표시할 흐름 데이터가 없습니다.",
      emptyDescription:
        "선택한 시나리오에서는 Sankey 흐름과 상태 막대를 빈 상태로 표시합니다.",
      previousYear: "2025년",
      currentYear: "2026년",
      unitLabel: "(명)",
      tooltipUnitLabel: "명",
      numberLocale: "ko-KR",
      nodeLabels: {
        A: "좋음",
        C: "보통",
        D2: "나쁨",
        "A-1": "좋음",
        "C-1": "보통",
        "D2-1": "나쁨",
      },
      legendItems: {
        veryImproved: "매우 완화",
        improved: "완화",
        noChange: "변화없음",
        worsened: "악화",
        veryWorsened: "매우 악화",
      },
    },
    timeline: {
      editorLabel: "Timeline 항목 편집 목록",
      editorTitle: "Timeline Items",
      editorDescription:
        "왼쪽 리스트에서 제목, 그룹, 기간, 상태, 표시 여부를 수정합니다.",
      visibleLabel: "표시",
      titleLabel: "제목",
      groupLabel: "그룹",
      statusLabel: "상태",
      startLabel: "시작 시간",
      endLabel: "종료 시간",
      progressLabel: "진행률",
      shellLabel: "포트폴리오 roadmap timeline",
      summary: (visibleCount, groupCount) =>
        `${visibleCount}개 항목 표시 / ${groupCount}개 그룹`,
      zoomControlsLabel: "Timeline 확대 축소 컨트롤",
      zoomInLabel: "확대",
      zoomOutLabel: "축소",
      resetLabel: "초기화",
      sidebarLabel: "구분",
      axisHeaderLabel: "구분 \\ 시간",
      momentLocale: "ko",
      markerLabels: {
        now: "지금",
        today: "오늘",
      },
      statusLabels: {
        done: "완료",
        active: "진행 중",
        planned: "예정",
      },
    },
    customGrid: {
      ariaLabel: "포트폴리오 custom data grid",
      searchLabel: "검색",
      searchPlaceholder: "역량 또는 작업 검색",
      categoryLabel: "카테고리",
      allCategoriesLabel: "전체 카테고리",
      selectedLabel: (count) => `${count}개 선택`,
      editModeLabel: "수정 모드",
      editModeNote: "수정 모드: 리스트를 수정 해서 저장 할수 있습니다.",
      cancelLabel: "취소",
      saveLabel: "저장",
      addRowLabel: "추가",
      addChildLabel: "추가",
      deleteSelectedLabel: "선택 삭제",
      newRowLabel: "신규",
      newChildLabel: "하위 추가",
      newRowPlaceholder: {
        capability: "새 역량",
        category: "카테고리",
        owner: "담당자",
        coverage: "0",
      },
      newChildPlaceholder: {
        name: "하위 작업",
        owner: "담당자",
      },
      clearLabel: "초기화",
      expandLabel: "펼치기",
      collapseLabel: "접기",
      selectAllLabel: "현재 보이는 행 전체 선택",
      selectRowLabel: (capability) => `${capability} 선택`,
      toggleRowLabel: (action, capability) => `${capability} ${action}`,
      headers: {
        capability: "역량",
        category: "카테고리",
        owner: "담당",
        status: "상태",
        coverage: "커버리지",
        updated: "업데이트",
      },
      impactSuffix: "영향도",
      addedChildNote: "수정 모드에서 추가한 항목입니다.",
      statusLabels: {
        Stable: "안정",
        Improving: "개선 중",
        Review: "검토",
      },
      impactLabels: {
        High: "높음",
        Medium: "중간",
        Low: "낮음",
      },
    },
    apiPlayground: {
      sourceBadge: "공공데이터 검색",
      endpointBadge: "KorService2/searchKeyword2",
      contractBadge: "detailCommon2 상세 조회",
      searchCard: {
        title: "관광 정보 검색 API 테스트",
        description:
          "검색어로 관광 정보 검색 API를 호출하고 응답을 화면용 결과로 변환합니다.",
      },
      responseCard: {
        title: "API 응답 결과",
        description:
          "요청 상태, 응답 시간, 자동 페이지네이션, 선택한 결과의 detailCommon2 상세 응답을 확인합니다.",
      },
      failureDemo: {
        label: "실패 처리 시나리오",
        description:
          "시나리오를 선택하는 순간 결과 영역과 요청 로그가 갱신됩니다. 관광정보 검색 버튼을 누르면 다시 실제 호출 모드로 전환됩니다.",
        emptyKeyword: "결과가존재하지않는검색어",
        serviceErrorCodeLabel: "오류 종류",
        unavailableErrorTitle: "분류만 가능한 오류",
        unavailableErrorDescription:
          "트래픽 초과, 만료키, IP 제한, 서버 내부 오류처럼 현재 데모에서 의도적으로 실제 발생시키기 어려운 코드는 문서 기준으로만 분류했습니다.",
        unavailableErrorListLabel: "오류 리스트 보기",
        retryLabel: "같은 조건으로 다시 시도",
        liveModeLabel: "정상 호출 모드로 전환",
        options: {
          live: {
            label: "실제 호출",
            description: "현재 인증키와 네트워크 상태로 공공데이터 API를 호출합니다.",
            logMessage: "실제 API 응답을 기준으로 상태를 표시합니다.",
            recovery:
              "실패 시 인증키, 요청 파라미터, 네트워크 상태를 확인한 뒤 다시 시도합니다.",
          },
          serviceError: {
            label: "API 결과 코드 오류",
            description: "선택한 resultCode를 정규화된 에러로 표시합니다.",
            logMessage:
              "선택한 공공데이터 resultCode 응답을 화면용 에러로 정규화했습니다.",
            recovery:
              "인증키와 필수 파라미터를 확인하고 사용자에게 재입력 또는 관리자 확인을 안내합니다.",
          },
          networkError: {
            label: "네트워크 실패 재현",
            description: "외부 API endpoint에 도달하지 못한 상황을 재현합니다.",
            logMessage:
              "네트워크 실패를 API_NETWORK_ERROR로 정규화했습니다.",
            recovery:
              "일시적인 연결 문제로 보고 재시도 버튼을 제공하며, 반복되면 잠시 후 다시 시도하도록 안내합니다.",
            tooltip:
              "브라우저에서 안정적으로 실제 네트워크 차단을 만들 수 없어서 정규화된 네트워크 실패 상태를 즉시 재현합니다.",
          },
          timeout: {
            label: "응답 지연 설명",
            description: "요청 제한 시간을 넘긴 상황을 설명용 상태로 보여줍니다.",
            logMessage: "요청 시간 초과를 API_TIMEOUT으로 정규화했습니다.",
            recovery:
              "중복 요청을 막고 같은 조건 재시도 또는 검색 조건 축소를 안내합니다.",
            tooltip:
              "GitHub Pages 데모에서 실제 timeout을 안정적으로 만들기는 어렵기 때문에, timeout 정규화 결과와 대처 방식을 설명 상태로 보여줍니다.",
          },
          empty: {
            label: "빈 결과 실제 호출",
            description:
              "검색어 결과가존재하지않는검색어로 실제 API를 호출해 빈 결과를 확인합니다.",
            logMessage:
              "HTTP/API 응답은 성공했지만 화면에 표시할 관광 정보가 없습니다.",
            recovery:
              "다른 검색어를 제안하고 요청 자체는 성공했음을 명확히 구분합니다.",
          },
        },
      },
      requestLog: {
        title: "요청 로그와 대처",
        description:
          "최근 검색 요청의 endpoint, 정규화된 에러 코드, 재시도 가능 여부, 사용자 대처를 한 번에 보여줍니다.",
        labels: {
          endpoint: "Endpoint",
          scenario: "시나리오",
          phase: "상태",
          statusCode: "상태 코드",
          errorCode: "에러 코드",
          retryable: "재시도",
          message: "정규화 메시지",
          recovery: "대처",
        },
        idleMessage: "아직 실행된 요청이 없습니다.",
        loadingMessage: "요청을 보내고 응답을 기다리는 중입니다.",
        successMessage: (count, total) =>
          `정상 응답을 화면용 목록 ${count}개로 변환했습니다. 전체 ${total}개입니다.`,
        emptyMessage: "요청은 성공했지만 화면에 표시할 결과가 없습니다.",
        retryableYes: "가능",
        retryableNo: "불가",
        notApplicable: "-",
        noActionNeeded: "정상 응답입니다. 별도 대처가 필요하지 않습니다.",
      },
      loadMoreLabel: "자동으로 다음 페이지 조회",
      loadingLabel: "응답을 기다리는 중입니다.",
      loadingMoreLabel: "다음 페이지를 불러오는 중입니다.",
      idleTitle: "API 테스트 대기 중",
      idleDescription: "검색어를 입력하면 관광 정보 검색 API를 호출합니다.",
      errorTitle: "요청에 실패했습니다.",
      canceledTitle: "요청이 취소되었습니다.",
      canceledDescription:
        "이전 요청은 화면 상태를 덮어쓰지 않도록 취소 처리했습니다.",
      endLabel: "모든 검색 결과를 불러왔습니다.",
      phaseLabels: {
        idle: "대기",
        loading: "로딩",
        success: "성공",
        empty: "빈 데이터",
        error: "에러",
        canceled: "취소됨",
      },
      metaLabels: {
        requestId: "요청 ID",
        keyword: "검색어",
        page: "페이지",
        latency: "응답 시간",
        startedAt: "요청 시각",
        completedAt: "응답 시각",
      },
      latencyUnit: "ms",
      publicData: {
        keywordLabel: "검색어",
        keywordPlaceholder: "예: 서울, 부산, 시장, 한옥",
        searchLabel: "관광정보 검색",
        noticeTitle: "GitHub Pages 정적 배포 방식",
        noticeDescription:
          "인증키는 프론트 빌드에 포함될 수 있으므로 데모용 키만 사용하고, 실제 서비스에서는 serverless proxy로 숨기는 구조가 필요합니다.",
        emptyTitle: "검색 결과가 없습니다.",
        emptyDescription:
          "다른 키워드로 다시 검색하거나 공공데이터 응답 상태를 확인하세요.",
        missingKeyTitle: "공공데이터 인증키가 필요합니다.",
        missingKeyDescription:
          "로컬 또는 배포 환경에 REACT_APP_DATA_GO_KR_SERVICE_KEY를 설정해야 실제 API를 호출할 수 있습니다.",
        imageFallback: "이미지 없음",
        addressFallback: "주소 정보 없음",
        contentTypeLabel: "콘텐츠 타입",
        contentTypeCodeLabel: "국문 코드",
        multilingualCodeLabel: "다국어 코드",
        regionLabel: "지역 코드",
        categoryLabel: "분류 코드",
        classificationLabel: "분류체계",
        legalDistrictLabel: "법정동 코드",
        legalRegionCodeLabel: "법정동 시도",
        legalDistrictCodeLabel: "시군구",
        modifiedLabel: "최근 갱신",
        detailEyebrow: "선택한 관광 정보",
        detailLoadingLabel: "KorService2/detailCommon2 공통정보를 조회하는 중입니다.",
        detailErrorTitle: "공통정보 상세 조회에 실패했습니다.",
        addressDetailLabel: "주소",
        overviewLabel: "개요",
        phoneLabel: "전화번호",
        createdLabel: "등록일",
        homepageLabel: "홈페이지",
        zipCodeLabel: "우편번호",
        coordinatesLabel: "좌표",
        coordinatesFallback: "좌표 정보 없음",
        mapLinkLabel: "네이버 지도에서 보기",
        mapLinkAriaLabel: (title) => `${title} 위치를 네이버 지도에서 열기`,
        languageNoticeLabel: "API 원문 안내",
        languageNoticeTooltip:
          "관광지명과 주소는 한국관광공사 API 응답 원문 기준으로 제공됩니다.",
        detailCloseLabel: "상세 닫기",
        totalLabel: (count, total) => `${total}개 중 ${count}개 표시`,
        sourceLabel: "한국관광공사 OpenAPI",
        detailSourceLabel: "KorService2/detailCommon2",
      },
    },
    dataGridCard: {
      title: "커스텀 데이터 그리드",
      description:
        "AccordionTable과 RealGrid 경험을 더미 데이터로 재구성한 검색, 정렬, 필터, 선택 예제입니다.",
    },
    publishedWorks: {
      roleLabel: "담당한 역할",
      technologiesLabel: "사용 기술",
      implementationPointsLabel: "구현 포인트",
      openLinkLabel: "외부 링크 열기",
      unavailableLinkLabel: "링크 확인 중",
      demoRoleLabel: "데모 앱은 역량 시연",
      publishedRoleLabel: "배포 사이트는 실제 결과물",
    },
    componentLab: {
      positioningTitle: "공통 UI를 직접 설계한 기준을 보여줍니다",
      positioningDescription:
        "이 포트폴리오 데모에서는 컴포넌트 API 설계, 접근성 연결, 검증 상태, 반응형 대응 역량을 보여주기 위해 핵심 Form UI를 직접 구현했습니다.",
      positioningItems: [
        "프로젝트 안에서 필요한 상호작용을 직접 설계",
        "label, helper, error, aria-describedby를 컴포넌트 내부에서 연결",
        "API 검색과 데이터그리드 편집 UI에 실제 적용",
      ],
      fieldCard: {
        title: "입력 필드",
        description:
          "검색, 기본 입력, 숫자, 여러 줄 입력, 선택 필드가 같은 label/helper 구조를 공유합니다.",
      },
      validationCard: {
        title: "검증 상태",
        description:
          "필수 입력, 오류 메시지, 비활성 상태를 같은 컴포넌트 API로 처리합니다.",
      },
      choiceCard: {
        title: "선택 컨트롤",
        description:
          "체크박스, 스위치, 세그먼트, 라디오 그룹도 helper text와 접근성 설명을 함께 제공합니다.",
      },
      previewCard: {
        title: "컴포넌트 설계 기준",
        description:
          "직접 구현한 공통 UI가 어떤 평가 지점을 보여주는지 요약합니다.",
      },
      fields: {
        keywordLabel: "검색어",
        keywordPlaceholder: "예: 관광 API",
        keywordHelper: "SearchField는 input type과 접근성 설명을 함께 관리합니다.",
        textLabel: "기본 텍스트",
        textPlaceholder: "예: 컴포넌트 설계",
        textHelper: "TextField는 일반 입력, 필수 표시, 오류 상태를 같은 API로 처리합니다.",
        textareaLabel: "상세 설명",
        textareaPlaceholder: "상태 변화와 사용 맥락을 입력합니다.",
        textareaHelper: "TextareaField는 긴 문장 입력과 helper/error 연결을 처리합니다.",
        numberLabel: "검증 점수",
        numberHelper: "NumberField는 숫자 범위 입력을 공통 필드 스타일로 맞춥니다.",
        selectLabel: "상태",
        selectHelper: "SelectField는 네이티브 select가 아닌 직접 구현한 listbox이며, ref로 외부 클릭을 감지해 닫습니다.",
        disabledLabel: "비활성 필드",
        disabledValue: "읽기 전용 상태",
        disabledHelper: "권한 또는 로딩 상태에서 입력을 막는 경우를 표현합니다.",
        requiredLabel: "필수 입력",
        requiredPlaceholder: "비워 둔 오류 상태",
        requiredError: "필수 값이 비어 있습니다.",
        checkboxLabel: "저장 전 변경사항 확인",
        checkboxHelper: "CheckboxField는 label과 helper text를 함께 클릭 가능하게 묶습니다.",
        switchLabel: "자동 저장",
        switchHelper: "SwitchField는 on/off 설정을 명확한 시각 상태로 표현합니다.",
        segmentedLabel: "화면 밀도",
        segmentedHelper: "SegmentedControl은 작은 옵션 집합을 빠르게 전환할 때 사용합니다.",
        radioLegend: "컴포넌트 타입",
        radioHelper: "RadioGroup은 fieldset/legend 구조로 컴포넌트 타입 선택 묶음을 표현합니다.",
      },
      selectOptions: [
        { value: "stable", label: "안정" },
        { value: "review", label: "검토 필요" },
        { value: "blocked", label: "차단" },
      ],
      radioOptions: [
        {
          value: "field",
          label: "Field",
          description: "입력 컴포넌트 묶음입니다.",
        },
        {
          value: "overlay",
          label: "Overlay",
          description: "Popover, Tooltip 같은 떠 있는 UI입니다.",
        },
        {
          value: "feedback",
          label: "Feedback",
          description: "Snackbar 같은 상태 피드백 UI입니다.",
        },
      ],
      segmentedOptions: [
        { value: "compact", label: "압축" },
        { value: "comfortable", label: "보통" },
        { value: "spacious", label: "넓게" },
      ],
      previewLabels: {
        componentApi: "Props API",
        accessibility: "A11y 연결",
        responsive: "반응형 입력",
        validation: "검증 상태",
      },
    },
    nav: [
      { id: "home", label: "홈", href: "#/", path: "/" },
      { id: "charts", label: "차트", href: "#/charts", path: "/charts" },
      {
        id: "data-grid",
        label: "데이터 그리드",
        href: "#/data-grid",
        path: "/data-grid",
      },
      {
        id: "timeline",
        label: "타임라인",
        href: "#/timeline",
        path: "/timeline",
      },
      {
        id: "api-playground",
        label: "API 테스트",
        href: "#/api-playground",
        path: "/api-playground",
      },
      {
        id: "component-lab",
        label: "컴포넌트",
        href: "#/component-lab",
        path: "/component-lab",
      },
      { id: "works", label: "배포 작업물", href: "#/works", path: "/works" },
    ],
    pages: {
      charts: {
        eyebrow: "차트 페이지",
        title: "차트 구현 패턴을 한곳에 모았습니다",
        description:
          "ECharts 래퍼, 옵션 생성 함수, 다국어 문구, 범례 상태, 크기 변경 처리를 차트 예제별로 분리해 보여줍니다.",
      },
      dataGrid: {
        eyebrow: "데이터 그리드 페이지",
        title: "그리드의 상호작용을 보여줍니다",
        description:
          "검색, 필터, 정렬, 부모/자식 선택, 편집 모드 흐름을 분리된 페이지에서 확인할 수 있습니다.",
      },
      timeline: {
        eyebrow: "타임라인 페이지",
        title: "일정 편집과 타임라인 흐름을 함께 보여줍니다",
        description:
          "왼쪽 편집 패널과 react-calendar-timeline 캔버스를 함께 배치해 이동, 리사이즈, 확대/축소 흐름을 확인합니다.",
      },
      apiPlayground: {
        eyebrow: "API 테스트 페이지",
        title: "실제 API 호출 흐름을 테스트합니다",
        description:
          "관광 정보 검색 API 호출, 자동 페이지네이션, 응답 정규화, 결과 상세 표시 흐름을 한 페이지에서 확인합니다.",
      },
      componentLab: {
        eyebrow: "컴포넌트 페이지",
        title: "직접 구현한 Form UI 시스템을 보여줍니다",
        description:
          "접근성·검증·반응형 상태를 갖춘 공통 입력 컴포넌트를 직접 구현하고 실제 화면에 적용했습니다.",
      },
      works: {
        eyebrow: "배포 작업물 페이지",
        title: "배포한 작업물을 정리했습니다",
        description:
          "이 페이지는 실제 공개 결과물과 담당 범위, 기술 스택, 구현 포인트를 분리해서 보여줍니다.",
      },
    },
    demoRoutes: {
      eyebrow: "바로가기",
      title: "데모와 실제 결과물",
      description:
        "차트, 그리드, 타임라인 데모와 배포 작업물을 바로 열어볼 수 있습니다.",
      openLabel: "페이지 열기",
    },
    sections: {
      overview: {
        eyebrow: "화면 문제",
        title: "해결하려는 화면 문제",
        description:
          "차트, 일정, 그리드가 한 화면에 섞일 때 생기는 읽기, 조작, 유지보수 문제를 더미 데이터로 재구성했습니다.",
      },
      skills: {
        eyebrow: "구현 역량",
        title: "구현으로 보여주는 프론트엔드 역량",
        description:
          "각 역량은 실제 화면과 코드에서 확인할 수 있는 구현 근거를 짧은 항목으로 정리했습니다.",
      },
      charts: {
        eyebrow: "차트 쇼케이스",
        title: "ECharts 차트 아카이브 데모",
        description:
          "포트폴리오의 차트 공통화 경험을 바탕으로 bar+line, dataZoom, scatter, boxplot, pie, treemap, sankey 예제를 구성했습니다.",
      },
      timeline: {
        eyebrow: "로드맵",
        title: "react-calendar-timeline 예제",
        description:
          "왼쪽 리스트에서 타임라인 항목을 수정하고, 확대/축소와 항목 이동/리사이즈 흐름을 확인할 수 있습니다.",
      },
      table: {
        eyebrow: "데이터 그리드",
        title: "커스텀 데이터 그리드",
        description:
          "계층형 데이터, 검색, 필터, 정렬, 선택, 편집 흐름을 포함한 포트폴리오형 더미 데이터 그리드를 보여줍니다.",
      },
      apiPlayground: {
        eyebrow: "API 테스트",
        title: "외부 API 연동 테스트",
        description:
          "관광 정보 검색 API를 호출하고, 응답 정규화와 결과 상세 표시까지 이어지는 흐름을 보여줍니다.",
      },
      componentLab: {
        eyebrow: "컴포넌트 시스템",
        title: "Custom Form UI Lab",
        description:
          "직접 구현한 입력, 선택, 날짜, 피드백 컴포넌트의 상태와 실제 상호작용 방식을 보여줍니다.",
      },
      publishedWorks: {
        eyebrow: "실제 결과물",
        title: "배포 사이트 아카이브",
        description:
          "사이트별 설명, 역할 범위 초안, 사용 기술, 구현 포인트, 외부 링크와 스크린샷 상태를 한 카드에서 확인할 수 있습니다.",
      },
    },
    tableHeaders: {
      project: "프로젝트",
      domain: "도메인",
      status: "상태",
      leadTime: "리드타임",
      chartCoverage: "차트 커버리지",
      apiContract: "API 계약",
      updatedAt: "업데이트",
      leadTimeUnitLabel: "일",
      statusLabels: {
        Stable: "안정",
        Improving: "개선 중",
        Watch: "관찰",
      },
    },
    infiniteTable: {
      title: "무한 렌더링 시연 테이블",
      description:
        "스크롤 하단에 닿으면 mock API에서 다음 페이지를 가져와 행을 추가하는 lazy loading 예제입니다.",
      ariaLabel: "무한 렌더링 mock API 호출 테이블",
      loadingLabel: "첫 페이지를 불러오는 중입니다.",
      loadingMoreLabel: "다음 항목을 불러오는 중입니다.",
      loadMoreLabel: "더 불러오기",
      endLabel: "모든 더미 항목을 불러왔습니다.",
      emptyLabel: "표시할 항목이 없습니다.",
      rowCountLabel: (count, total) => `${total}개 중 ${count}개 표시`,
      headers: {
        screen: "화면 시나리오",
        module: "모듈",
        status: "상태",
        requestCount: "요청 수",
        loadedAt: "로드 시각",
      },
      requestCountUnitLabel: "회",
      statusLabels: {
        Queued: "대기",
        Rendering: "렌더링 중",
        Complete: "완료",
      },
    },
  },
  en: {
    appName: "Ryu Hojin",
    appSubtitle: "Frontend Portfolio",
    navigationLabel: "Primary pages",
    homeLabel: "Go to home",
    heroEyebrow: "Frontend Portfolio",
    heroTitle: "I make dense product screens easier to read",
    heroBody:
      "A React and TypeScript portfolio demo for chart-heavy operations screens, timelines, and custom grids, rebuilt with dummy data and clear component boundaries.",
    heroCtaLabel: "View demos",
    heroRefreshLabel: "Reload demo data",
    portfolioLinkLabel: "Notion Portfolio",
    portfolioFocusLabel: "Portfolio focus",
    portfolioFocus: [
      "Chart system",
      "Custom UI",
      "Gantt timeline",
      "Custom grid",
    ],
    previewLabel: "Portfolio screen preview",
    previewTechLabel: "React + TypeScript",
    updatedLabel: "Updated",
    dateTimeLocale: "en-US",
    heroStats: {
      chartCases: "Chart cases",
      timelineItems: "Timeline items",
      gridRows: "Grid rows",
    },
    languageLabel: "Change language",
    refreshLabel: "Refresh data",
    loading: "Loading dashboard data.",
    errorTitle: "Failed to load dashboard data.",
    homeProblems: [
      {
        id: "dense-data-ui",
        label: "Dense data screens",
        value: "Readable layout",
        helper:
          "Charts, tables, and status summaries keep a clear information order.",
      },
      {
        id: "editable-grid-flow",
        label: "Editable grid flows",
        value: "Predictable state",
        helper:
          "Search, filters, selection, and edit mode stay understandable together.",
      },
      {
        id: "timeline-workbench",
        label: "Timeline workbench",
        value: "Move and resize",
        helper:
          "Date ranges, visible windows, and item edits are handled separately.",
      },
      {
        id: "responsive-safety",
        label: "Responsive safety",
        value: "No overflow",
        helper:
          "Charts and grids stay readable on small screens with scroll and fallback text.",
      },
    ],
    homeCapabilities: [
      {
        id: "component-boundaries",
        category: "Component Boundaries",
        highlights: [
          "section assembly",
          "feature ownership",
          "custom Form UI",
        ],
      },
      {
        id: "data-shaping",
        category: "Data Shaping",
        highlights: [
          "mock API boundary",
          "view model formatting",
          "typed responses",
        ],
      },
      {
        id: "interactive-state",
        category: "Interactive State",
        highlights: [
          "chart legend state",
          "grid edit mode",
          "timeline controls",
        ],
      },
      {
        id: "accessibility-responsive",
        category: "Accessible Responsive UI",
        highlights: [
          "accessible labels",
          "fallback descriptions",
          "mobile scroll guards",
        ],
      },
    ],
    chartCards: {
      businessTrend: {
        title: "Bar + Line",
        description:
          "Monthly metrics and conversion rate are generated from one option builder.",
        ariaLabel: "Monthly revenue, active users, and conversion rate chart",
        fallbackDescription:
          "A combined bar and line chart comparing monthly product metrics.",
      },
      implementationTrend: {
        title: "Portfolio Chart Trend",
        description:
          "Previous and current values are compared with review scores, while longer data can be explored with dataZoom.",
        ariaLabel: "Portfolio chart implementation trend chart",
        fallbackDescription:
          "A mixed chart comparing previous and current values with review scores as a line.",
      },
      qualityScatter: {
        title: "Quality Scatter",
        description:
          "Cycle time, defect rate, and complexity are compared with a scatter chart and visualMap.",
        ariaLabel: "Feature quality and complexity scatter chart",
        fallbackDescription:
          "A scatter chart comparing each feature by cycle time, defect rate, and complexity.",
      },
      genderBoxPlot: {
        title: "Gender Box Plot",
        description:
          "A dummy boxplot example comparing distributions by gender and age group with outlier points.",
        ariaLabel: "Gender and age group distribution boxplot chart",
        fallbackDescription:
          "A boxplot chart comparing minimum, quartiles, median, maximum, and outliers by gender and age group.",
      },
      capabilityTreemap: {
        title: "Capability Treemap",
        description:
          "A treemap example that reshapes the chart archive by implementation area and relative weight.",
        ariaLabel: "Portfolio visualization capability treemap",
        fallbackDescription:
          "A treemap showing the share of ECharts, markup charts, and shared UX work.",
      },
      categoryShare: {
        title: "Pie Chart",
        description:
          "A pie chart with legend and labels summarizes the chart example mix.",
        ariaLabel: "Chart category share pie chart",
        fallbackDescription:
          "A donut chart showing the share of chart example categories.",
      },
      sankey: {
        title: "Custom Component + Sankey Flow",
        description:
          "The flow separates left and right status bars, transition lines, legend, and values into a custom component.",
      },
    },
    chartOptions: {
      businessTrend: {
        emptyLabel: "No chart data to display.",
        revenueIndex: "Revenue index",
        activeUsers: "Active users",
        conversionRate: "Conversion rate",
        revenueAxis: "Revenue",
        conversionAxis: "Conversion",
      },
      implementationTrend: {
        emptyLabel: "No chart data to display.",
        previous: "Previous",
        current: "Current",
        reviewScore: "Review score",
        casesAxis: "Cases",
        reviewAxis: "Review",
      },
      qualityScatter: {
        emptyLabel: "No chart data to display.",
        cycleTimeAxis: "Cycle time",
        defectRateAxis: "Defect rate",
        complexityHigh: "High complexity",
        complexityLow: "Low",
        seriesName: "Feature quality",
      },
      genderBoxPlot: {
        emptyLabel: "No chart data to display.",
        valueAxis: "Value",
        seriesName: "Gender distribution",
        numberLocale: "en-US",
        genderLabels: {
          Male: "Male",
          Female: "Female",
        },
        statsLabels: {
          min: "Min",
          q1: "Q1",
          median: "Median",
          q3: "Q3",
          max: "Max",
          outlier: "Outlier",
        },
      },
      categoryShare: {
        emptyLabel: "No chart data to display.",
        centerLabel: "Example",
        seriesName: "Chart archive",
        numberLocale: "en-US",
      },
    },
    chartScenarios: {
      label: "Chart scenario",
      cardLabel: "Card scenario",
      options: {
        normal: {
          label: "Normal",
          description: "Dummy data changes naturally when the charts refresh.",
        },
        empty: {
          label: "Empty data",
          description: "Check every chart empty state at once.",
        },
      },
      edgeCaseOptions: {
        normal: {
          label: "Normal",
          description: "Show the default dummy data.",
        },
        missingXAxis: {
          label: "Missing x-axis values",
          description: "Blank out a random x-axis point.",
        },
        zeroValues: {
          label: "Zero values",
          description: "Set a random item to zero.",
        },
        singleSlice: {
          label: "Single pie slice",
          description: "Show one active pie slice.",
        },
        smallSankeyValues: {
          label: "Small Sankey values",
          description: "Show clustered small flow values.",
        },
        outlierHeavyBoxplot: {
          label: "Outlier-heavy boxplot",
          description: "Show a heavier outlier distribution.",
        },
      },
    },
    sankey: {
      ariaLabel: "Year over year status transition sankey chart",
      legendLabel: "Sankey legend",
      fallbackDescription:
        "A flow chart showing how 2025 A, C, and D2 statuses move into 2026 A, C, and D2 statuses.",
      emptyTitle: "No flow data to display.",
      emptyDescription:
        "This scenario shows the custom Sankey component in its empty state.",
      previousYear: "2025",
      currentYear: "2026",
      unitLabel: "(people)",
      tooltipUnitLabel: " people",
      numberLocale: "en-US",
      nodeLabels: {
        A: "Good",
        C: "Normal",
        D2: "Poor",
        "A-1": "Good",
        "C-1": "Normal",
        "D2-1": "Poor",
      },
      legendItems: {
        veryImproved: "Greatly improved",
        improved: "Improved",
        noChange: "No change",
        worsened: "Worsened",
        veryWorsened: "Greatly worsened",
      },
    },
    timeline: {
      editorLabel: "Timeline item edit list",
      editorTitle: "Timeline Items",
      editorDescription:
        "Edit title, group, dates, status, and visibility from the left list.",
      visibleLabel: "Visible",
      titleLabel: "Title",
      groupLabel: "Group",
      statusLabel: "Status",
      startLabel: "Start time",
      endLabel: "End time",
      progressLabel: "Progress",
      shellLabel: "Portfolio roadmap timeline",
      summary: (visibleCount, groupCount) =>
        `${visibleCount} visible items / ${groupCount} groups`,
      zoomControlsLabel: "Timeline zoom controls",
      zoomInLabel: "Zoom In",
      zoomOutLabel: "Zoom Out",
      resetLabel: "Reset",
      sidebarLabel: "Group",
      axisHeaderLabel: "Group \\ Time",
      momentLocale: "en",
      markerLabels: {
        now: "Now",
        today: "Today",
      },
      statusLabels: {
        done: "Done",
        active: "Active",
        planned: "Planned",
      },
    },
    customGrid: {
      ariaLabel: "Portfolio custom data grid",
      searchLabel: "Search",
      searchPlaceholder: "Search capability or task",
      categoryLabel: "Category",
      allCategoriesLabel: "All categories",
      selectedLabel: (count) => `${count} selected`,
      editModeLabel: "Edit mode",
      editModeNote: "Edit mode: update the list and save your changes.",
      cancelLabel: "Cancel",
      saveLabel: "Save",
      addRowLabel: "Add",
      addChildLabel: "Add",
      deleteSelectedLabel: "Delete selected",
      newRowLabel: "New",
      newChildLabel: "New child",
      newRowPlaceholder: {
        capability: "New capability",
        category: "Category",
        owner: "Owner",
        coverage: "0",
      },
      newChildPlaceholder: {
        name: "Child task",
        owner: "Owner",
      },
      clearLabel: "Clear",
      expandLabel: "Expand",
      collapseLabel: "Collapse",
      selectAllLabel: "Select all visible rows",
      selectRowLabel: (capability) => `Select ${capability}`,
      toggleRowLabel: (action, capability) => `${action} ${capability}`,
      headers: {
        capability: "Capability",
        category: "Category",
        owner: "Owner",
        status: "Status",
        coverage: "Coverage",
        updated: "Updated",
      },
      impactSuffix: "impact",
      addedChildNote: "Added in edit mode.",
      statusLabels: {
        Stable: "Stable",
        Improving: "Improving",
        Review: "Review",
      },
      impactLabels: {
        High: "High",
        Medium: "Medium",
        Low: "Low",
      },
    },
    apiPlayground: {
      sourceBadge: "Public data search",
      endpointBadge: "KorService2/searchKeyword2",
      contractBadge: "detailCommon2 detail fetch",
      searchCard: {
        title: "Tourism Search API Test",
        description:
          "Calls the tourism search API and maps the response into UI-ready results.",
      },
      responseCard: {
        title: "API Response Result",
        description:
          "Shows request state, latency, automatic pagination, and the selected result's detailCommon2 response.",
      },
      failureDemo: {
        label: "Failure handling scenario",
        description:
          "Selecting a scenario updates the result area and request log immediately. The tourism search button switches back to live API mode.",
        emptyKeyword: "결과가존재하지않는검색어",
        serviceErrorCodeLabel: "Error type",
        unavailableErrorTitle: "Classified only",
        unavailableErrorDescription:
          "Traffic-limit, expired-key, IP-restriction, and internal-server errors are classified from the manual because this demo cannot intentionally trigger them reliably.",
        unavailableErrorListLabel: "View error list",
        retryLabel: "Retry with the same conditions",
        liveModeLabel: "Switch to live API mode",
        options: {
          live: {
            label: "Live request",
            description:
              "Calls the public data API with the current key and network state.",
            logMessage: "State is based on the live API response.",
            recovery:
              "If it fails, check the service key, request parameters, and network state before retrying.",
          },
          serviceError: {
            label: "API result-code error",
            description:
              "Shows the selected resultCode as a normalized UI error.",
            logMessage:
              "The selected public data resultCode was normalized into a UI error.",
            recovery:
              "Check the service key and required parameters, then guide the user to retry or contact an administrator.",
          },
          networkError: {
            label: "Network failure replay",
            description:
              "Replays a case where the endpoint cannot be reached.",
            logMessage:
              "The network failure was normalized as API_NETWORK_ERROR.",
            recovery:
              "Treat it as a temporary connection issue, provide retry, and guide the user to try again later if it repeats.",
            tooltip:
              "A static browser demo cannot reliably block the real network on demand, so this immediately replays the normalized network-failure state.",
          },
          timeout: {
            label: "Timeout explanation",
            description:
              "Shows the state for a request that exceeded the expected response time.",
            logMessage: "The timeout was normalized as API_TIMEOUT.",
            recovery:
              "Prevent duplicate requests and guide the user to retry or narrow the search conditions.",
            tooltip:
              "A GitHub Pages demo cannot reliably create a real timeout, so this explains the normalized timeout result and recovery path.",
          },
          empty: {
            label: "Live empty result",
            description:
              "Calls the real API with keyword 결과가존재하지않는검색어 to confirm an empty result.",
            logMessage:
              "The HTTP/API response succeeded, but there are no tourism items to display.",
            recovery:
              "Suggest another keyword while making it clear that the request itself succeeded.",
          },
        },
      },
      requestLog: {
        title: "Request Log And Recovery",
        description:
          "Shows the latest endpoint, normalized error code, retryability, and user recovery action.",
        labels: {
          endpoint: "Endpoint",
          scenario: "Scenario",
          phase: "Phase",
          statusCode: "Status code",
          errorCode: "Error code",
          retryable: "Retryable",
          message: "Normalized message",
          recovery: "Recovery",
        },
        idleMessage: "No request has been executed yet.",
        loadingMessage: "The request was sent and is waiting for a response.",
        successMessage: (count, total) =>
          `The response was mapped into ${count} UI-ready rows out of ${total}.`,
        emptyMessage: "The request succeeded, but there are no results to show.",
        retryableYes: "Yes",
        retryableNo: "No",
        notApplicable: "-",
        noActionNeeded: "The response is healthy. No recovery action is required.",
      },
      loadMoreLabel: "Auto-load next page",
      loadingLabel: "Waiting for the response.",
      loadingMoreLabel: "Loading the next page.",
      idleTitle: "Waiting for an API test",
      idleDescription: "Enter a keyword to call the tourism search API.",
      errorTitle: "Request failed.",
      canceledTitle: "Request canceled.",
      canceledDescription:
        "The previous request was canceled so it cannot overwrite the screen state.",
      endLabel: "All search results have been loaded.",
      phaseLabels: {
        idle: "Idle",
        loading: "Loading",
        success: "Success",
        empty: "Empty",
        error: "Error",
        canceled: "Canceled",
      },
      metaLabels: {
        requestId: "Request ID",
        keyword: "Keyword",
        page: "Page",
        latency: "Latency",
        startedAt: "Started",
        completedAt: "Completed",
      },
      latencyUnit: "ms",
      publicData: {
        keywordLabel: "Keyword",
        keywordPlaceholder: "e.g. Seoul, Busan, market, hanok",
        searchLabel: "Search tourism data",
        noticeTitle: "GitHub Pages static deployment",
        noticeDescription:
          "The service key can be included in the frontend bundle, so this demo uses a demo key. Production services should hide it behind a serverless proxy.",
        emptyTitle: "No search results.",
        emptyDescription:
          "Try another keyword or check the public data API response state.",
        missingKeyTitle: "Public data service key required.",
        missingKeyDescription:
          "Set REACT_APP_DATA_GO_KR_SERVICE_KEY locally or in the deployment environment to call the real API.",
        imageFallback: "No image",
        addressFallback: "No address",
        contentTypeLabel: "Content type",
        contentTypeCodeLabel: "Korean code",
        multilingualCodeLabel: "Multilingual code",
        regionLabel: "Region code",
        categoryLabel: "Category code",
        classificationLabel: "Classification",
        legalDistrictLabel: "Legal district code",
        legalRegionCodeLabel: "Legal region",
        legalDistrictCodeLabel: "District",
        modifiedLabel: "Last updated",
        detailEyebrow: "Selected tourism item",
        detailLoadingLabel: "Loading KorService2/detailCommon2 common details.",
        detailErrorTitle: "Failed to load common details.",
        addressDetailLabel: "Address",
        overviewLabel: "Overview",
        phoneLabel: "Phone",
        createdLabel: "Created",
        homepageLabel: "Homepage",
        zipCodeLabel: "Zip code",
        coordinatesLabel: "Coordinates",
        coordinatesFallback: "No coordinates",
        mapLinkLabel: "Open in Naver Map",
        mapLinkAriaLabel: (title) => `Open ${title} in Naver Map`,
        languageNoticeLabel: "Original API text",
        languageNoticeTooltip:
          "Place names and addresses come from the Korea Tourism OpenAPI response, so result text may remain in Korean while the interface is in English.",
        detailCloseLabel: "Close details",
        totalLabel: (count, total) => `${count} of ${total} rows shown`,
        sourceLabel: "Korea Tourism OpenAPI",
        detailSourceLabel: "KorService2/detailCommon2",
      },
    },
    dataGridCard: {
      title: "Custom Data Grid",
      description:
        "A dummy-data reconstruction of AccordionTable and RealGrid patterns with search, sorting, filters, and selection.",
    },
    publishedWorks: {
      roleLabel: "My role",
      technologiesLabel: "Technologies",
      implementationPointsLabel: "Implementation points",
      openLinkLabel: "Open external link",
      unavailableLinkLabel: "Link pending",
      demoRoleLabel: "Demo app shows capability",
      publishedRoleLabel: "Published sites show outcomes",
    },
    componentLab: {
      positioningTitle: "Common UI design criteria",
      positioningDescription:
        "For this portfolio demo, I implemented the core Form UI directly to demonstrate component API design, accessibility wiring, validation states, and responsive behavior.",
      positioningItems: [
        "Interaction patterns designed directly inside this project",
        "label, helper, error, and aria-describedby are connected inside the components",
        "Applied to the API search form and editable data grid UI",
      ],
      fieldCard: {
        title: "Input Fields",
        description:
          "Search, text, number, textarea, and select fields share the same label/helper structure.",
      },
      validationCard: {
        title: "Validation States",
        description:
          "Required fields, error messages, and disabled states use one component API.",
      },
      choiceCard: {
        title: "Choice Controls",
        description:
          "Checkbox, switch, segmented control, and radio group controls include helper text and accessible grouping.",
      },
      previewCard: {
        title: "Component Design Criteria",
        description:
          "A short summary of what the custom common UI system demonstrates.",
      },
      fields: {
        keywordLabel: "Keyword",
        keywordPlaceholder: "Example: tourism API",
        keywordHelper: "SearchField manages the input type and accessibility text together.",
        textLabel: "Text input",
        textPlaceholder: "Example: component design",
        textHelper: "TextField handles common input, required markers, and error states with one API.",
        textareaLabel: "Description",
        textareaPlaceholder: "Describe the state change and usage context.",
        textareaHelper: "TextareaField handles longer content with helper/error wiring.",
        numberLabel: "Review score",
        numberHelper: "NumberField keeps numeric range inputs aligned with the common field style.",
        selectLabel: "Status",
        selectHelper: "SelectField is a custom listbox, not a native select. It closes with ref-based outside-click detection.",
        disabledLabel: "Disabled field",
        disabledValue: "Read-only state",
        disabledHelper: "Represents blocked input during permission or loading states.",
        requiredLabel: "Required field",
        requiredPlaceholder: "Empty error state",
        requiredError: "This value is required.",
        checkboxLabel: "Review changes before saving",
        checkboxHelper: "CheckboxField keeps the label and helper text in one control.",
        switchLabel: "Auto save",
        switchHelper: "SwitchField expresses on/off settings with a clear visual state.",
        segmentedLabel: "Screen density",
        segmentedHelper: "SegmentedControl is useful for compact option sets.",
        radioLegend: "Component type",
        radioHelper: "RadioGroup uses fieldset and legend for grouped component type choices.",
      },
      selectOptions: [
        { value: "stable", label: "Stable" },
        { value: "review", label: "Needs review" },
        { value: "blocked", label: "Blocked" },
      ],
      radioOptions: [
        {
          value: "field",
          label: "Field",
          description: "A group of input components.",
        },
        {
          value: "overlay",
          label: "Overlay",
          description: "Floating UI such as Popover and Tooltip.",
        },
        {
          value: "feedback",
          label: "Feedback",
          description: "State feedback UI such as Snackbar.",
        },
      ],
      segmentedOptions: [
        { value: "compact", label: "Compact" },
        { value: "comfortable", label: "Comfort" },
        { value: "spacious", label: "Spacious" },
      ],
      previewLabels: {
        componentApi: "Props API",
        accessibility: "A11y wiring",
        responsive: "Responsive inputs",
        validation: "Validation state",
      },
    },
    nav: [
      { id: "home", label: "Home", href: "#/", path: "/" },
      { id: "charts", label: "Charts", href: "#/charts", path: "/charts" },
      {
        id: "data-grid",
        label: "Data Grid",
        href: "#/data-grid",
        path: "/data-grid",
      },
      {
        id: "timeline",
        label: "Timeline",
        href: "#/timeline",
        path: "/timeline",
      },
      {
        id: "api-playground",
        label: "API Test",
        href: "#/api-playground",
        path: "/api-playground",
      },
      {
        id: "component-lab",
        label: "Components",
        href: "#/component-lab",
        path: "/component-lab",
      },
      { id: "works", label: "Works", href: "#/works", path: "/works" },
    ],
    pages: {
      charts: {
        eyebrow: "Charts Page",
        title: "Validate chart implementation cases in one place",
        description:
          "Each example keeps the ECharts wrapper, option builder, i18n labels, legend state, and resize handling separated.",
      },
      dataGrid: {
        eyebrow: "Data Grid Page",
        title: "Inspect operational grid interactions separately",
        description:
          "Search, filtering, sorting, parent/child selection, and edit mode flows can be checked on a focused page.",
      },
      timeline: {
        eyebrow: "Timeline Page",
        title: "Show timeline editing and canvas interactions apart",
        description:
          "The editor panel and react-calendar-timeline canvas sit together so move, resize, and zoom flows are easy to verify.",
      },
      apiPlayground: {
        eyebrow: "API Test Page",
        title: "Test real API request flows",
        description:
          "Inspect tourism search API calls, automatic pagination, response normalization, and result details in one page.",
      },
      componentLab: {
        eyebrow: "Components Page",
        title: "Inspect the custom Form UI system",
        description:
          "This page shows directly implemented common input components with accessibility, validation, responsive states, and real screen usage.",
      },
      works: {
        eyebrow: "Published Works Page",
        title: "Published work examples in one place",
        description:
          "This page shows public work examples with role scope, tech stack, and implementation points.",
      },
    },
    demoRoutes: {
      eyebrow: "Quick Links",
      title: "Demos and Real Outcomes",
      description:
        "Open chart, grid, timeline demos and published work examples from here.",
      openLabel: "Open page",
    },
    sections: {
      overview: {
        eyebrow: "UI Problems",
        title: "Screen Problems This Demo Solves",
        description:
          "The demo rebuilds readability, interaction, and maintenance problems that appear when charts, timelines, and grids share one product surface.",
      },
      skills: {
        eyebrow: "Implementation Capability",
        title: "Frontend Capability Shown in Code",
        description:
          "Each capability is backed by concrete implementation evidence visible in the UI and code.",
      },
      charts: {
        eyebrow: "ECharts Showcase",
        title: "ECharts Archive Demo",
        description:
          "Bar+line, dataZoom, scatter, boxplot, pie, treemap, and sankey examples share a wrapper and option-builder pattern.",
      },
      timeline: {
        eyebrow: "Roadmap",
        title: "react-calendar-timeline Editor",
        description:
          "Timeline items can be edited from the left list, zoomed, moved, and resized in the timeline shell.",
      },
      table: {
        eyebrow: "Data Grid",
        title: "Custom Data Grid",
        description:
          "A dummy portfolio data grid shows nested rows, search, filters, sorting, selection, and edit-mode flows.",
      },
      apiPlayground: {
        eyebrow: "API Test",
        title: "External API Integration Test",
        description:
          "Tourism search API results are normalized and shown with selectable detail data.",
      },
      componentLab: {
        eyebrow: "Component System",
        title: "Custom Form UI Lab",
        description:
          "Shows the states and real interactions of directly implemented input, selection, date, and feedback components.",
      },
      publishedWorks: {
        eyebrow: "Real Outcomes",
        title: "Published Site Archive",
        description:
          "Each card shows the site summary, role scope, tech stack, implementation points, external link, and screenshot status.",
      },
    },
    tableHeaders: {
      project: "Project",
      domain: "Domain",
      status: "Status",
      leadTime: "Lead time",
      chartCoverage: "Chart coverage",
      apiContract: "API contract",
      updatedAt: "Updated",
      leadTimeUnitLabel: "d",
      statusLabels: {
        Stable: "Stable",
        Improving: "Improving",
        Watch: "Watch",
      },
    },
    infiniteTable: {
      title: "Infinite Render Demo Table",
      description:
        "A lazy-loading table that calls the mock API for the next page and appends rows when the scroll reaches the bottom.",
      ariaLabel: "Infinite render mock API table",
      loadingLabel: "Loading the first page.",
      loadingMoreLabel: "Loading more rows.",
      loadMoreLabel: "Load more",
      endLabel: "All dummy rows have been loaded.",
      emptyLabel: "No rows to display.",
      rowCountLabel: (count, total) => `${count} of ${total} rows shown`,
      headers: {
        screen: "Screen scenario",
        module: "Module",
        status: "Status",
        requestCount: "Requests",
        loadedAt: "Loaded at",
      },
      requestCountUnitLabel: "x",
      statusLabels: {
        Queued: "Queued",
        Rendering: "Rendering",
        Complete: "Complete",
      },
    },
  },
};
